import React, { useState, useContext, useEffect, useRef } from "react";
import {
  LoadingOverlay,
  FocusTrap,
  Text,
  PinInput,
  Button,
  Group,
  Stack,
  Center,
  ThemeIcon,
  Box,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { IconShieldCheck, IconPhoneCheck } from "@tabler/icons-react";
import { DataContext } from "../../context/DataContext";
import { isMobile } from "react-device-detect";
import useStatusCheck from "../../hooks/useStatusCheck";
import { useMediaQuery } from "@mantine/hooks";
import useAdjustInputSize from "../../hooks/useAdjustInputSize";
import FloatingLabelInput from "../../components/floating-label-input";
import DateOfBirthSelect from "../../components/date-of-birth-select";
import AddressInput from "../../components/address-input";
import UkAddressInput from "../../components/uk-address-input";
export default function PhoneMatch() {
  const { data, dispatch, text, routeSequence, currentIndex, navigateToNext, settings } = useContext(DataContext);
  const [loading, setLoading] = useState(true);
  const [isOtpInput, setIsOtpInput] = useState(false);
  const [template, setTemplate] = useState({});
  const [result, setResult] = useState(false);
  const [hasHandledFailure, setHasHandledFailure] = useState(false);
  const smallScreen = useMediaQuery("(max-width: 40em)");
  const isIframe = window !== window.top;
  const inputSize = useAdjustInputSize();
  const firstNameInputRef = useRef(null);

  const [activeStep, setActiveStep] = useState(0);
  const collectDob = true;
  const [collectAddress, setCollectAddress] = useState(false);
  const [collectSsn, setCollectSsn] = useState(false);
  const [debugInfo, setDebugInfo] = useState({});
  const currentPath = "phonematch";

  const hasAdditionalFields = collectAddress || collectSsn;

  useEffect(() => {
    const matchedRoute = settings?.journey?.route?.find(
      (route) => route.path === currentPath
    );
    const inputs = matchedRoute?.inputs || [];
    const isUKAddressSetting =
      settings?.journey?.settings?.isUKAddress ||
      settings?.journey?.settings?.isUkAddress ||
      matchedRoute?.settings?.isUKAddress ||
      matchedRoute?.settings?.isUkAddress;
    // Only collect address if explicitly in inputs array
    const addressNeeded = inputs.includes("address") || inputs.includes("fulladdress") || inputs.includes("residence");
    const ssnNeeded = inputs.includes("ssn") || inputs.includes("last4ssn") || inputs.includes("lastfourssn") || inputs.includes("ssn4");
    setCollectAddress(addressNeeded);
    setCollectSsn(ssnNeeded);
    setDebugInfo({
      isUKAddressSetting,
      inputs,
      addressNeeded,
      ssnNeeded,
      journeySettings: settings?.journey?.settings,
      matchedRoute,
    });
  }, [settings, currentPath]);

  // Check if this component is skippable based on route settings
  const currentRoute = routeSequence[currentIndex];
  const isSkippable = currentRoute?.skipable === true;

  // Determine which fields should be disabled from route configuration
  const disabledFields = Array.isArray(currentRoute?.component?.disabled)
    ? currentRoute.component.disabled
    : Array.isArray(currentRoute?.disabled)
    ? currentRoute.disabled
    : [];

  const getNewPin = () => {
    setIsOtpInput(false);
    setTemplate({});
    setResult(false);
    form.setFieldValue("pin", "");
  };

  // Handler for skip button
  const handleSkip = () => {
    // Update session state to mark phonematch as complete
    const updatedSessionState = {
      ...data.sessionState,
      phonematch: "COMPLETED"
    };

    // Update the session state
    dispatch({
      type: "SET_SESSION_STATE",
      payload: updatedSessionState
    });

    // Mark current component as complete to allow navigation
    dispatch({
      type: "SET_IS_CURRENT_COMPONENT_COMPLETE",
      payload: true
    });

    // Trigger navigation to the next step
    dispatch({ type: "TRIGGER_NAVIGATION" });
  };

  useStatusCheck(result, false, "phonematch");

  const form = useForm({
    initialValues: {
      fn: data.fn || "",
      ln: data.ln || "",
      phone: data.phone || "",
      dob: data.dob || "",
      addr: data.addr || "",
      city: data.city || "",
      state: data.state || "",
      zip: data.zip || "",
      ssn: data.ssn || "",
      pin: "",
    },
    validate: (values) => {
      if (isOtpInput) {
        return {
          pin: values.pin.length < 6 ? text.phonematch.code.error : null,
        };
      }
      const errors = {};

      if (
        !disabledFields.includes("phone") &&
        values.phone.replace(/\D/g, "").length !== 10
      ) {
        errors.phone = text.general.phoneError;
      }

      if (!disabledFields.includes("fn") && values.fn.length < 2) {
        errors.fn = text.general.firstNameError;
      }

      if (!disabledFields.includes("ln") && values.ln.length < 2) {
        errors.ln = text.general.lastNameError;
      }

      if (collectDob && activeStep === 0 && !disabledFields.includes("dob")) {
        const dobPattern =
          /^(19|20)\d{2}(0[1-9]|1[0-2])(0[1-9]|[12][0-9]|3[01])$/;
        const isDobFormatCorrect = dobPattern.test(values.dob);
        let dobError = text.general.dateOfBirthError;

        if (isDobFormatCorrect) {
          const year = parseInt(values.dob.substring(0, 4), 10);
          const month = parseInt(values.dob.substring(4, 6), 10) - 1;
          const day = parseInt(values.dob.substring(6, 8), 10);
          const date = new Date(year, month, day);
          if (
            date.getFullYear() === year &&
            date.getMonth() === month &&
            date.getDate() === day
          ) {
            dobError = null;
          }
        }
        errors.dob = dobError;
      }

      if (collectAddress && activeStep === 1 && !disabledFields.includes("addr")) {
        if (
          !values.addr ||
          !values.city ||
          !values.state ||
          values.zip.trim().length !== 5
        ) {
          errors.addr = "Address is required";
        } else {
          errors.addr = null;
        }
      }

      if (collectSsn && activeStep === 1 && !disabledFields.includes("ssn")) {
        if (/[^0-9]/.test(values.ssn) || values.ssn.length !== 4) {
          errors.ssn = text.general.lastFourSSNError;
        } else {
          errors.ssn = null;
        }
      }

      return errors;
    },
  });

  const handleDobChange = (dob) => {
    form.setFieldValue("dob", dob.year + dob.month + dob.day);
  };

  const handleAddressChange = (address) => {
    form.setFieldValue("addr", address.addr);
    form.setFieldValue("city", address.city);
    form.setFieldValue("state", address.state);
    form.setFieldValue("zip", address.zip);
  };

  const isUKAddressSetting = debugInfo.isUKAddressSetting;
  const AddressComponent = isUKAddressSetting
    ? UkAddressInput
    : AddressInput;

  const handleNext = () => {
    const validation = form.validate();
    if (
      !validation.errors.fn &&
      !validation.errors.ln &&
      !validation.errors.phone &&
      (!collectDob || !validation.errors.dob)
    ) {
      setActiveStep(1);
      form.clearErrors();
    }
  };

  const submitHandler = () => {
    const errors = form.validate();

    if (!errors.hasErrors) {
      setLoading(true);
      setHasHandledFailure(false); // Reset failure handling state on new request
      if (!disabledFields.includes("phone")) {
        data.phone = form.values.phone;
      }
      if (!disabledFields.includes("fn")) {
        data.fn = form.values.fn;
      }
      if (!disabledFields.includes("ln")) {
        data.ln = form.values.ln;
      }
      if (collectDob && !disabledFields.includes("dob")) {
        data.dob = form.values.dob;
      }
      if (collectAddress && !disabledFields.includes("addr")) {
        data.addr = form.values.addr;
        data.city = form.values.city;
        data.state = form.values.state;
        data.zip = form.values.zip;
      }
      if (collectSsn && !disabledFields.includes("ssn")) {
        data.ssn = form.values.ssn;
      }
      data.isOtpInput = isOtpInput;
      if (isOtpInput) {
        template.codematch.key = form.values.pin;
        data.phonematch_continue = template;
      }

      fetch(
        process.env.REACT_APP_BACKEND_URL +
          "/v3/stable/api/frontend/phonematch",
        {
          method: "POST",
          body: JSON.stringify(data),
        }
      )
        .then(function (res) {
          return res.json();
        })
        .then(function (data) {
          if (data.result.action === "PENDING" && !isOtpInput) {
            setLoading(false);
            setTemplate(data.continuations.outofband.template);
            setIsOtpInput(true);
          } else if (data.result.action === "PASS") {
            setLoading(true);
            setResult(true);
            //setResultStatus(data.result.action);
          } else {
            // For failed results, keep loading state true to prevent form from showing
            // Only trigger status check once for failed results to prevent infinite polling
            if (!hasHandledFailure) {
              setResult(true);
              setHasHandledFailure(true);
              // Mark component as complete to allow navigation
              dispatch({ type: "SET_IS_CURRENT_COMPONENT_COMPLETE", payload: true });
            }
            //setResultStatus(data.result.action);
          }
        })
        .catch(function () {
          alert("Something Went Wrong! Please try again.");
        });
    }
  };

  useEffect(() => {
    setLoading(false);
    form.clearErrors();
  }, [hasAdditionalFields]);

  if (loading) {
    return <LoadingOverlay visible />;
  }

  return (
    <>
      <form
        onSubmit={form.onSubmit(submitHandler)}
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between", // This will push the button to the bottom
          height: "100%", // This should take the full height of its parent
        }}
      >
        <Box>
          {!isOtpInput && (
            <>
              <Stack spacing={8} mt={inputSize === "md" ? 5 : 5}>
                <Center>
                  <ThemeIcon
                    variant="light"
                    c="teal"
                    size={inputSize === "md" ? 60 : 55}
                    radius={"xl"}
                  >
                    <IconPhoneCheck
                      size={inputSize === "md" ? 40 : 35}
                      stroke={1.5}
                      color="teal"
                    />
                  </ThemeIcon>
                </Center>

                <Stack spacing={0} mt="sm">
                  <Text fz={inputSize === "md" ? 24 : 20} fw={700}>
                    {text.phonematch.title}
                  </Text>

                  <Text c="dimmed" fz={inputSize}>
                    {text.phonematch.subTitle}
                  </Text>
                </Stack>
              </Stack>

              {hasAdditionalFields ? (
                <Stack mt={20} spacing={"md"}>
                  {activeStep === 0 && (
                    <>
                      <Box>
                        <FloatingLabelInput
                          size={"lg"}
                          label={text.general.firstName}
                          placeholder={text.general.firstNamePlaceholder}
                          {...form.getInputProps("fn")}
                          disabled={disabledFields.includes("fn")}
                        />
                      </Box>
                      <Box>
                        <FloatingLabelInput
                          size={"lg"}
                          label={text.general.lastName}
                          {...form.getInputProps("ln")}
                          disabled={disabledFields.includes("ln")}
                        />
                      </Box>
                      {collectDob && (
                        <Box>
                          <DateOfBirthSelect
                            isSinglePageForm={true}
                            onDobChange={handleDobChange}
                            {...form.getInputProps("dob")}
                            disabled={disabledFields.includes("dob")}
                          />
                        </Box>
                      )}
                      <Box>
                        <FloatingLabelInput
                          size={"lg"}
                          label={text.general.phone}
                          tooltip={text.general.phoneTooltip}
                          placeholder={text.general.phonePlaceholder}
                          inputMode="tel"
                          {...form.getInputProps("phone")}
                          disabled={disabledFields.includes("phone")}
                          customOnChange={(event) => {
                            let value = event.target.value || "";
                            value = value.replace(/\D/g, "").substring(0, 10);
                            value = value.replace(
                              /(\d{3})(\d{3})(\d{4})/,
                              "$1-$2-$3"
                            );
                            form.getInputProps("phone").onChange(value);
                            form.setFieldValue("phone", value);
                          }}
                        />
                      </Box>
                    </>
                  )}
                  {activeStep === 1 && (
                    <>
                      {collectAddress && (
                        <Box>
                          <AddressComponent
                            onAddressChange={handleAddressChange}
                            value={{
                              addr: form.values.addr,
                              city: form.values.city,
                              state: form.values.state,
                              zip: form.values.zip,
                            }}
                            error={form.errors.addr}
                            {...form.getInputProps("addr")}
                          />
                        </Box>
                      )}
                      {collectSsn && (
                        <Box>
                          <FloatingLabelInput
                            size={"lg"}
                            label={text.general.lastFourSSN}
                            isMaskedInput
                            maxLength={4}
                            inputMode="numeric"
                            pattern="[0-9]*"
                            {...form.getInputProps("ssn")}
                            disabled={disabledFields.includes("ssn")}
                          />
                        </Box>
                      )}
                    </>
                  )}
                </Stack>
              ) : (
                <Stack mt={20} spacing={"md"}>
                  <Box>
                    <FloatingLabelInput
                      size={"lg"}
                      label={text.general.firstName}
                      placeholder={text.general.firstNamePlaceholder}
                      {...form.getInputProps("fn")}
                      disabled={disabledFields.includes("fn")}
                    />
                  </Box>
                  <Box>
                    <FloatingLabelInput
                      size={"lg"}
                      label={text.general.lastName}
                      {...form.getInputProps("ln")}
                      disabled={disabledFields.includes("ln")}
                    />
                  </Box>
                  {collectDob && (
                    <Box>
                      <DateOfBirthSelect
                        isSinglePageForm={true}
                        onDobChange={handleDobChange}
                        {...form.getInputProps("dob")}
                        disabled={disabledFields.includes("dob")}
                      />
                    </Box>
                  )}
                  <Box>
                    <FloatingLabelInput
                      size={"lg"}
                      label={text.general.phone}
                      tooltip={text.general.phoneTooltip}
                      placeholder={text.general.phonePlaceholder}
                      inputMode="tel"
                      {...form.getInputProps("phone")}
                      disabled={disabledFields.includes("phone")}
                      customOnChange={(event) => {
                        let value = event.target.value || "";
                        value = value.replace(/\D/g, "").substring(0, 10);
                        value = value.replace(
                          /(\d{3})(\d{3})(\d{4})/,
                          "$1-$2-$3"
                        );
                        form.getInputProps("phone").onChange(value);
                        form.setFieldValue("phone", value);
                      }}
                    />
                  </Box>
                </Stack>
              )}
            </>
          )}
          {isOtpInput && (
            <>
              <Center mt={inputSize === "md" ? 25 : 10}>
                <Stack align="center">
                  <ThemeIcon
                    variant="light"
                    c="teal"
                    size={inputSize === "md" ? 70 : 65}
                    radius={"xl"}
                  >
                    <IconShieldCheck
                      stroke={1}
                      size={inputSize === "md" ? 50 : 45}
                      color="teal"
                    />
                  </ThemeIcon>

                  <Text fz={inputSize === "md" ? 30 : 23} fw={700}>
                    {text.phonematch.code.title}
                  </Text>
                  <Text
                    ta="center"
                    c="dimmed"
                    fz={inputSize === "md" ? 24 : 14}
                  >
                    {text.phonematch.code.subTitle}
                  </Text>
                </Stack>
              </Center>

              <Group position="center" mt={40}>
                <FocusTrap>
                  <PinInput
                    data-autofocus
                    type="number"
                    oneTimeCode
                    size={"xl"}
                    length={6}
                    {...form.getInputProps("pin")}
                  />
                </FocusTrap>
              </Group>
              <Stack align="center" mt={40} spacing={0}>
                <Text fz="xs" c="dimmed">
                  {text.phonematch.code.newCodeNote}
                </Text>
                <Button onClick={getNewPin} variant="subtle">
                  {text.phonematch.code.newCodeButton}
                </Button>
              </Stack>
            </>
          )}
        </Box>
        <Box pb="sm">
          <Stack spacing={0}>
            {!isOtpInput && hasAdditionalFields ? (
              activeStep === 0 ? (
                <Button mt="xl" fullWidth size={"lg"} onClick={handleNext}>
                  Next
                </Button>
              ) : (
                <Button mt="xl" fullWidth type="submit" size={"lg"}>
                  {text.phonematch.button}
                </Button>
              )
            ) : (
              <Button mt="xl" fullWidth type="submit" size={"lg"}>
                {isOtpInput ? text.phonematch.code.button : text.phonematch.button}
              </Button>
            )}

            {hasAdditionalFields && !isOtpInput && activeStep === 1 && (
              <Button
                variant="subtle"
                color="gray"
                fullWidth
                onClick={() => setActiveStep(0)}
                size="xs"
                compact
                mt={8}
              >
                Back
              </Button>
            )}

            {isSkippable && !isOtpInput && activeStep === 0 && (
              <Button
                variant="subtle"
                color="gray"
                fullWidth
                onClick={handleSkip}
                size="xs"
                compact
                mt={8}
              >
                {currentRoute?.skipableText || "Skip this step"}
              </Button>
            )}
          </Stack>
        </Box>
      </form>
    </>
  );
}
